import { useState } from 'react'
import { Button, Select, Card, Skeleton, Modal, message } from 'antd'
import { ArrowRightOutlined, DownOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { categoryApi } from '../services/categories'
import { hackathonApi, HackathonResource } from '../services/hackathon'
import { resourceApi, Resource } from '../services/resources'

const { Option } = Select

const Resources = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeCategory, setActiveCategory] = useState('all')
  const [selectedType, setSelectedType] = useState('all')
  const [resourceType, setResourceType] = useState('general') // 'general' or 'hackathon'


  // React Query hooks
  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoryApi.getCategories(),
    select: (data) => data.success ? data.data : []
  })

  const { data: hackathonsData, isLoading: hackathonsLoading } = useQuery({
    queryKey: ['hackathons'],
    queryFn: () => hackathonApi.getHackathons(),
    select: (data) => data.success ? data.data : []
  })

  // Automatically detect active hackathon
  const activeHackathon = hackathonsData?.find(hackathon => hackathon.isActive === true)

  // Get general resources
  const { data: generalResourcesData, isLoading: generalResourcesLoading } = useQuery({
    queryKey: ['general-resources', activeCategory, selectedType],
    queryFn: () => resourceApi.getResources({
      categoryId: activeCategory === 'all' ? undefined : activeCategory,
      resourceType: selectedType === 'all' ? undefined : selectedType,
      limit: 100 // Get more resources for better UX
    }),
    select: (data) => data.success ? data.data : [],
    enabled: resourceType === 'general'
  })

  const { data: hackathonResourcesData, isLoading: hackathonResourcesLoading } = useQuery({
    queryKey: ['hackathon-resources', activeHackathon?._id, activeCategory, selectedType],
    queryFn: () => hackathonApi.getHackathonResources(activeHackathon!._id, {
      category: activeCategory === 'all' ? undefined : activeCategory,
      type: selectedType === 'all' ? undefined : selectedType,
    }),
    select: (data) => {
      if (data.success && data.data && Array.isArray(data.data.resources)) {
        return data.data.resources;
      }
      return [];
    },
    enabled: !!activeHackathon?._id && resourceType === 'hackathon'
  })

  // Delete general resource mutation
  const deleteGeneralResourceMutation = useMutation({
    mutationFn: resourceApi.deleteResource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['general-resources'] })
      message.success('Resource deleted successfully!')
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete resource')
    },
  })

  // Delete hackathon resource mutation
  const deleteHackathonResourceMutation = useMutation({
    mutationFn: (resourceId: string) => hackathonApi.removeResourceFromHackathon(activeHackathon!._id, resourceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hackathon-resources'] })
      message.success('Resource removed from hackathon successfully!')
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to remove resource from hackathon')
    },
  })

  // Create categories array with "All" option
  const categoriesWithAll = [
    { key: 'all', label: 'All' },
    ...(categoriesData || []).map(category => ({
      key: category._id,
      label: category.name
    }))
  ]

  const types = [
    { key: 'all', label: 'All' },
    { key: 'guide', label: 'Guide' },
    { key: 'tutorial', label: 'Tutorial' },
    { key: 'documentation', label: 'Documentation' },
    { key: 'video', label: 'Video' },
    { key: 'article', label: 'Article' }
    
  ]

  // Get filtered resources based on resource type
  const filteredResources = resourceType === 'hackathon' 
      ? (Array.isArray(hackathonResourcesData) ? hackathonResourcesData : []) 
    : (Array.isArray(generalResourcesData) ? generalResourcesData : [])

  const handleDeleteResource = (resource: Resource | HackathonResource) => {
    Modal.confirm({
      icon: null,
      title: (
        <div style={{ textAlign: 'center' }}>
          <ExclamationCircleOutlined style={{ fontSize: 48, color: '#faad14', marginBottom: 16 }} />
          <div>Are you sure you want to delete this resource?</div>
        </div>
      ),
      content: (
        <div style={{ textAlign: 'center' }}>
          {resourceType === 'general' 
            ? `This will permanently delete "<b>${resource.title}</b>" and cannot be undone.`
            : `This will remove "<b>${resource.title}</b>" from the hackathon and cannot be undone.`
          }
        </div>
      ),
      footer: (
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }} className='mt-4'>
          <Button
            onClick={() => Modal.destroyAll()}
            style={{ width: '100%', height: '40px' }}
            className='!rounded-[10px] !h-9 !text-xs'
          >
            Cancel
          </Button>
          <Button
            type="primary"
            danger
            onClick={() => {
              if (resourceType === 'general') {
                deleteGeneralResourceMutation.mutate(resource._id)
              } else {
                deleteHackathonResourceMutation.mutate(resource._id)
              }
              Modal.destroyAll()
            }}
            loading={deleteGeneralResourceMutation.isPending || deleteHackathonResourceMutation.isPending}
            style={{ width: '100%', height: '40px' }}
            className='!rounded-[10px] !h-9 !text-xs'
          >
            Yes, Delete
          </Button>
        </div>
      ),
      centered: true,
    })
  }

  const handleEditResource = (resource: Resource | HackathonResource) => {
    // Navigate to edit page with resource ID and resource type
    const type = resourceType === 'hackathon' ? 'hackathon' : 'general'
    navigate(`/resources/edit-resource/${resource._id}?type=${type}`)
  }

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Resources</h1>

          <div className="flex items-center gap-4">

          <Button
            type="default"
            onClick={() => navigate('/resources/assign-resources')}
            className="!bg-transparent border border-primary text-primary hover:!bg-[#010ED0] hover:!text-white h-9 transition-colors"
            style={{
              borderColor: '#010ED0',
              color: '#010ED0',
            }}
          >
            Assign Resources
          </Button>
          <Button
            type="default"
            onClick={() => navigate('/resources/categories')}
            className="!bg-transparent border border-primary text-primary hover:!bg-[#010ED0] hover:!text-white h-9 transition-colors"
            style={{
              borderColor: '#010ED0',
              color: '#010ED0',
            }}
          >
            View Category
          </Button>

          
          <Button 
            type="primary" 
          
            onClick={() => navigate('/resources/add-resource')}
            className="bg-primary hover:bg-primary/90 h-9"
          >
            Add Resources
          </Button>

          </div>
        </div>

        {/* Resource Type Filters */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
        
            <Button
              type={resourceType === 'general' ? 'primary' : 'default'}
              onClick={() => {
                setResourceType('general')
                setActiveCategory('all')
                setSelectedType('all')
              }}
              className={`${
                resourceType === 'general'
                  ? 'bg-primary'
                  : 'bg-[#EEF1FA] text-gray-700'
              } rounded-[8px] px-4 py-2 h-8 text-sm font-medium !shadow-none !border-none`}
              style={{
                ...(resourceType === 'general'
                  ? { backgroundColor: '#010ED0' }
                  : { backgroundColor: '#EEF1FA', color: '#334155' }),
                transition: 'none'
              }}
            >
              General Resources
            </Button>

            <Button
              type={resourceType === 'hackathon' ? 'primary' : 'default'}
              onClick={() => {
                setResourceType('hackathon')
                setActiveCategory('all')
                setSelectedType('all')
              }}
              className={`${
                resourceType === 'hackathon'
                  ? 'bg-primary'
                  : 'bg-[#EEF1FA] text-gray-700'
              } rounded-[8px] px-4 py-2 h-8 text-sm font-medium !shadow-none !border-none`}
              style={{
                ...(resourceType === 'hackathon'
                  ? { backgroundColor: '#010ED0' }
                  : { backgroundColor: '#EEF1FA', color: '#334155' }),
                transition: 'none'
              }}
            >
              Hackathon Resources
            </Button>
          </div>
          

          <div className="flex items-center gap-4">
            {/* Category filter for both resource types */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Category:</span>
              <Select
                value={activeCategory}
                onChange={setActiveCategory}
                className="w-40"
                suffixIcon={<DownOutlined className="text-gray-400" />}
                loading={categoriesLoading}
              >
                {categoriesWithAll.map(category => (
                  <Option key={category.key} value={category.key}>
                    {category.label}
                  </Option>
                ))}
              </Select>
            </div>
            
            {/* Type filter for both hackathon and general resources */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Type:</span>
              <Select
                value={selectedType}
                onChange={setSelectedType}
                className="w-32"
                suffixIcon={<DownOutlined className="text-gray-400" />}
              >
                {types.map(type => (
                  <Option key={type.key} value={type.key}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Resources Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 flex-1">
        {resourceType === 'general' && generalResourcesLoading ? (
          // Skeleton for general resources
          <>
            {[...Array(6)].map((_, index) => (
              <Card key={index} className="bg-white border border-gray-200">
                <div className="flex flex-col gap-4">
                  <Skeleton.Button active size="small" style={{ width: 80, height: 24 }} />
                  <Skeleton.Input active size="default" style={{ height: 20 }} />
                  <Skeleton paragraph={{ rows: 3, width: ['100%', '90%', '80%'] }} active />
                  <Skeleton.Button active size="small" style={{ width: 100, height: 20 }} />
                </div>
              </Card>
            ))}
          </>
        ) : resourceType === 'hackathon' && hackathonResourcesLoading ? (
          // Skeleton for hackathon resources
          <>
            {[...Array(6)].map((_, index) => (
              <Card key={index} className="bg-white border border-gray-200">
                <div className="flex flex-col gap-4">
                  <Skeleton.Button active size="small" style={{ width: 80, height: 24 }} />
                  <Skeleton.Input active size="default" style={{ height: 20 }} />
                  <Skeleton paragraph={{ rows: 3, width: ['100%', '90%', '80%'] }} active />
                  <Skeleton.Button active size="small" style={{ width: 100, height: 20 }} />
                </div>
              </Card>
            ))}
          </>
        ) : resourceType === 'hackathon' && !activeHackathon ? (
          <div className="col-span-full flex justify-center items-center py-8">
            <div className="text-gray-500">No active hackathon found. Resources will be available when a hackathon is active.</div>
          </div>
        ) : filteredResources.length === 0 ? (
          <div className="col-span-full flex flex-col justify-center items-center py-16">
            <div className="text-center">
              <div className="text-gray-400 text-6xl mb-4">📚</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No resources found</h3>
              <p className="text-gray-500 mb-6">
                {resourceType === 'general' 
                  ? 'No general resources match your current filters.'
                  : 'No hackathon resources match your current filters.'
                }
              </p>
              <Button 
                type="primary" 
                onClick={() => navigate('/resources/add-resource')}
                className="bg-primary hover:bg-primary/90"
              >
                Add Resources
              </Button>
          </div>
          </div>
        ) : (
          filteredResources.map(resource => {
          // Handle both Resource and HackathonResource types
          const isHackathonResource = resourceType === 'hackathon'
          const title = resource.title
          const description = resource.description
          const link = resource.link
          const tag = resource.categoryId?.name || 'Resource'
          const id = resource._id

          return (
            <Card
              key={id}
              className="bg-white border border-gray-200 hover:shadow-md transition-shadow !h-fit"
            >
              <div className="flex flex-col gap-4">
                {/* Tag */}
                <div className="flex items-start justify-between">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                    {tag}
                  </span>
                </div>
                
                {/* Title */}
                <h3 className="text-base font-semibold text-gray-900">
                  {title}
                </h3>
                
                {/* Description */}
                <p className="text-sm text-gray-600 leading-relaxed">
                  {description}
                </p>
                
                {/* View Link and Actions */}
                <div className="w-full flex items-center justify-between">
                <a 
                  href={link}
                  className="text-primary text-sm font-medium hover:text-blue-700 flex items-center gap-1 w-fit"
                    target='_blank'
                >
                  View Link
                  <ArrowRightOutlined className="w-4 h-4" />
                </a>

                  <div className="flex items-center gap-2">
                    {resourceType === 'general' && (
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleEditResource(resource)
                        }}
                        className="text-gray-500 hover:text-primary p-1"
                      />
                    )}
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleDeleteResource(resource)
                      }}
                      className="text-gray-500 hover:text-red-500 p-1"
                    />
                  </div>
                </div>
              </div>
            </Card>
          )
        })
        )}
      </div>
    </div>
  )
}

export default Resources
